import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import { Recado } from "../componentes/ui";
import { lembrar, lerRelatorio, type EixoDoRelatorio, type Nivel, type Relatorio, type RespostaDoRelatorio } from "../lib/diagnostico";
import { Moldura } from "./Diagnostico";

// O relatório do Diagnóstico RT, pelo TOKEN do diagnóstico (000373/000374).
//
// As seções são as do app da Lovable, na mesma ordem: o resumo com o radar dos seis eixos, o mapa
// de vulnerabilidades, os alertas por resposta, o plano de 30, 60 e 90 dias e os indicadores. A
// diferença é de onde tudo vem: lá os textos, os pontos e a nota eram constantes do front e a conta
// era feita no navegador; aqui a página lê `drt_relatorio`, que traz a nota que o BANCO calculou e
// os textos das tabelas `drt_*`.
//
// Duas coisas que a página NÃO traz da Lovable, de propósito:
//   · o "Direcionamento na Pós", que lá aparecia só para quem o e-mail dizia ser aluno. Aqui a
//     página não sabe quem é aluno, e não deve saber (era a mesma consulta que dizia a qualquer um
//     se um e-mail estava cadastrado);
//   · o PDF desenhado no navegador (jsPDF). O relatório é IMPRESSO pelo navegador, como o
//     currículo (000100): o texto sai selecionável, e nada entra no bundle.
//
// Os diagnósticos das versões anteriores do questionário (166, de 19 e de 27 perguntas) abrem só
// com o resumo: as perguntas eram outras, e cruzar as respostas antigas com as de hoje daria
// alertas de perguntas que a pessoa não respondeu (000374).

// Os rótulos de cada nível nos alertas e no plano. O nível GERAL vem com o rótulo do banco
// (`drt_niveis.rotulo`); estes nomeiam o mesmo nível em cada resposta, como a Lovable fazia.
const ROTULO: Record<Nivel, string> = { alto: "Alto risco", medio: "Médio risco", baixo: "Baixo risco" };
const EM_PALAVRA: Record<Nivel, string> = { alto: "alto", medio: "médio", baixo: "baixo" };
const ORDEM: Record<Nivel, number> = { alto: 0, medio: 1, baixo: 2 };

// O tom de cada nível. Cor de interface que precisa ser vista nos DOIS temas é token semântico; e o
// nível vem sempre com a palavra ao lado, nunca só a cor.
const TOM: Record<Nivel, { chip: string; barra: string; caixa: string }> = {
  alto: { chip: "bg-erro-fundo text-erro-tinta", barra: "bg-erro-tinta", caixa: "border-erro-borda" },
  medio: { chip: "bg-alerta-fundo text-alerta-tinta", barra: "bg-alerta-tinta", caixa: "border-alerta-tinta/40" },
  baixo: { chip: "bg-aviso-fundo text-aviso-tinta", barra: "bg-aviso-tinta", caixa: "border-aviso-borda" },
};

// As três faixas do plano, com o subtítulo da Lovable. Qual pergunta cai em qual faixa é dado
// (`drt_prazos`); o nome da faixa é da página.
const FAIXAS: { chave: "30" | "60" | "90"; titulo: string; subtitulo: string }[] = [
  { chave: "30", titulo: "30 dias", subtitulo: "Base e fundamentos" },
  { chave: "60", titulo: "60 dias", subtitulo: "Consolidação e processos" },
  { chave: "90", titulo: "90 dias", subtitulo: "Estratégia e posicionamento" },
];

// Os indicadores de sucesso, iguais para todo mundo na Lovable (não dependem das respostas).
const METAS = ["Equipe capacitada", "ERP atualizado", "Contratos revisados", "Clientes mapeados"];
const RESULTADOS = ["Compliance total estabelecida", "Créditos identificados", "Simulações concluídas"];

// A página da Pós em Reforma Tributária, com a origem do clique marcada como o hub.
const POS_RT =
  "https://contabilidadefacilitada.com/lps/pos/reforma-tributaria/?utm_campaign=pos-rt&utm_source=hub&utm_medium=organico&utm_content=diagnostico&utm_term=tela-final";

export default function DiagnosticoRelatorio() {
  const { token = "" } = useParams();
  const [relatorio, setRelatorio] = useState<Relatorio | null | "falha" | "carregando">("carregando");

  useEffect(() => {
    let vivo = true;
    void lerRelatorio(token).then((r) => {
      if (vivo) setRelatorio(r);
    });
    return () => {
      vivo = false;
    };
  }, [token]);

  if (relatorio === "carregando") return <Carregando texto="Abrindo o relatório…" />;
  if (relatorio === "falha" || relatorio === null) {
    return (
      <Moldura>
        <div className="surface p-7 text-center" data-situacao={relatorio === null ? "link-invalido" : "sem-conexao"}>
          <h1 className="font-display text-xl font-bold text-titulo">
            {relatorio === null ? "Este link de relatório não é válido" : "Não conseguimos abrir o relatório agora"}
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-cinza">
            {relatorio === null
              ? "Confira se o endereço foi copiado inteiro. Se preferir, faça um novo diagnóstico: leva poucos minutos."
              : "Confira a conexão e recarregue a página. O link continua valendo."}
          </p>
          <Link to="/diagnostico" className="pill-cta mt-5">
            Fazer o diagnóstico
          </Link>
        </div>
      </Moldura>
    );
  }

  return (
    <Moldura largo>
      <RelatorioDoDiagnostico relatorio={relatorio} />
    </Moldura>
  );
}

// As seções, na ordem da Lovable. A escolhida mora no ENDEREÇO (`?secao=plano`), e não num estado:
// quem manda o link de uma seção a alguém manda a seção, e o botão de voltar do navegador volta a
// seção anterior. Uma por vez, como lá: com as cinco numa página só, cada pergunta aparecia duas
// vezes (nos alertas e no plano) e o relatório passava de 12 mil pixels (medido em 30/10).
const SECOES = [
  { chave: "resumo", rotulo: "Resumo" },
  { chave: "vulnerabilidades", rotulo: "Mapa de vulnerabilidades" },
  { chave: "alertas", rotulo: "Alertas de risco" },
  { chave: "plano", rotulo: "Plano de ação" },
  { chave: "indicadores", rotulo: "Indicadores" },
] as const;
type Secao = (typeof SECOES)[number]["chave"];

function RelatorioDoDiagnostico({ relatorio }: { relatorio: Relatorio }) {
  const area = useRef<HTMLDivElement>(null);
  const [busca] = useSearchParams();
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  // Na impressão saem TODAS as seções, no tema CLARO: papel é branco, e o texto claro do tema
  // escuro sumiria nele. `beforeprint` vale também para o Ctrl+P, não só para o botão, e o
  // `flushSync` faz a tela desenhar as seções antes de o navegador montar as folhas.
  const [imprimindo, setImprimindo] = useState(false);
  const atual = relatorio.versao === "atual";
  const pedida = busca.get("secao");
  const secao: Secao = SECOES.some((s) => s.chave === pedida) ? (pedida as Secao) : "resumo";

  useEffect(() => {
    const antes = () => {
      flushSync(() => setImprimindo(true));
      area.current?.classList.add("tema-claro");
    };
    const depois = () => {
      area.current?.classList.remove("tema-claro");
      setImprimindo(false);
    };
    window.addEventListener("beforeprint", antes);
    window.addEventListener("afterprint", depois);
    return () => {
      window.removeEventListener("beforeprint", antes);
      window.removeEventListener("afterprint", depois);
    };
  }, []);

  async function copiarLink() {
    try {
      await navigator.clipboard.writeText(window.location.href.split("?")[0]);
      setRecado({ tipo: "ok", texto: "Link copiado. Guarde-o: é por ele que você volta a este relatório." });
    } catch {
      setRecado({ tipo: "erro", texto: "Não foi possível copiar. Copie o endereço da barra do navegador." });
    }
  }

  const quando = new Date(relatorio.criado_em).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
  const mostrar = (s: Secao) => imprimindo || secao === s;

  return (
    <div ref={area} className="area-de-impressao relatorio-drt" data-relatorio={relatorio.versao} data-secao={atual ? secao : undefined}>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold tracking-widest text-cinza uppercase">Diagnóstico da Reforma Tributária</p>
          <h1 className="mt-1 font-display text-2xl font-bold text-titulo sm:text-3xl">Seu relatório</h1>
          <p className="mt-1 text-sm text-cinza">Feito em {quando}</p>
        </div>
        <div className="no-imprimir flex flex-wrap gap-2">
          <button type="button" className="btn-contorno min-h-10 text-sm" onClick={() => window.print()} data-imprimir>
            Imprimir ou salvar em PDF
          </button>
          <button type="button" className="btn-contorno min-h-10 text-sm" onClick={() => void copiarLink()} data-copiar-link>
            Copiar o link
          </button>
          <Link
            to="/diagnostico"
            className="btn-contorno min-h-10 text-sm"
            onClick={() => lembrar.guardarAndamento(null)}
            data-novo-diagnostico
          >
            Fazer outro diagnóstico
          </Link>
        </div>
      </header>
      {recado && (
        <div className="no-imprimir">
          <Recado tipo={recado.tipo} texto={recado.texto} />
        </div>
      )}

      {atual ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8">
          <nav className="no-imprimir" aria-label="Seções do relatório">
            <ul className="flex gap-2 overflow-x-auto pb-1 lg:sticky lg:top-6 lg:flex-col lg:overflow-visible lg:pb-0">
              {SECOES.map((s) => (
                <li key={s.chave} className="shrink-0">
                  <Link
                    to={`?secao=${s.chave}`}
                    replace
                    aria-current={secao === s.chave ? "page" : undefined}
                    className={`block rounded-xl px-3.5 py-2 text-sm font-semibold whitespace-nowrap transition ${
                      secao === s.chave ? "bg-acento text-acento-tinta" : "bg-chip text-chip-tinta hover:bg-tile"
                    }`}
                    data-ir-para={s.chave}
                  >
                    {s.rotulo}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-4 hidden text-xs leading-relaxed text-cinza lg:block">
              Na impressão saem todas as seções. Para guardar em PDF, escolha{" "}
              <strong className="font-semibold text-tinta">Salvar como PDF</strong> na janela de impressão.
            </p>
          </nav>
          <div className="min-w-0 [&>*:first-child]:mt-0">
            {mostrar("resumo") && <Resumo relatorio={relatorio} />}
            {mostrar("vulnerabilidades") && <Vulnerabilidades eixos={relatorio.eixos} />}
            {mostrar("alertas") && <Alertas relatorio={relatorio} />}
            {mostrar("plano") && <Plano relatorio={relatorio} />}
            {mostrar("indicadores") && <Indicadores />}
            {!imprimindo && <ProximaSecao secao={secao} />}
          </div>
        </div>
      ) : (
        <>
          <Resumo relatorio={relatorio} />
          <section className="surface mt-6 p-6" data-versao-anterior>
            <p className="text-sm leading-relaxed text-tinta">
              Este diagnóstico foi feito na versão anterior do questionário, que tinha{" "}
              {relatorio.versao === "19-perguntas" ? "19" : "27"} perguntas. O resultado está acima; o relatório
              detalhado — os alertas e o plano de ação — é o do questionário de hoje.
            </p>
            <Link to="/diagnostico" className="pill-cta no-imprimir mt-4" onClick={() => lembrar.guardarAndamento(null)}>
              Fazer o diagnóstico de hoje
            </Link>
          </section>
        </>
      )}

      <section className="painel-navy mt-8 rounded-3xl p-6 text-center sm:p-8" data-cta-pos>
        <p className="font-display text-lg font-bold text-white uppercase">Acelere a sua preparação</p>
        <p className="mx-auto mt-2 max-w-xl text-navy-mute">
          Pós em Reforma Tributária e Prática Fiscal da Faculdade Brasileira de Contabilidade
        </p>
        <a href={POS_RT} target="_blank" rel="noreferrer" className="pill-cta no-imprimir mt-5">
          Saiba mais sobre a pós
          <span className="sr-only"> (abre em outra aba)</span>
        </a>
      </section>
    </div>
  );
}

/** O caminho para a seção seguinte, no pé de cada uma: o relatório se lê na ordem. */
function ProximaSecao({ secao }: { secao: Secao }) {
  const i = SECOES.findIndex((s) => s.chave === secao);
  const proxima = SECOES[i + 1];
  if (!proxima) return null;
  return (
    <p className="no-imprimir mt-6 text-right">
      <Link to={`?secao=${proxima.chave}`} replace className="btn-contorno min-h-10 text-sm" data-proxima-secao={proxima.chave}>
        {proxima.rotulo} →
      </Link>
    </p>
  );
}

function Selo({ nivel, texto }: { nivel: Nivel; texto?: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${TOM[nivel].chip}`} data-nivel={nivel}>
      {texto ?? ROTULO[nivel]}
    </span>
  );
}

function Barra({ percentual, nivel }: { percentual: number; nivel: Nivel }) {
  return (
    <div className="h-2 overflow-hidden rounded-full bg-borda" aria-hidden="true">
      <div className={`h-full rounded-full ${TOM[nivel].barra}`} style={{ width: `${Math.max(0, Math.min(100, percentual))}%` }} />
    </div>
  );
}

function Resumo({ relatorio }: { relatorio: Relatorio }) {
  const { nivel, percentual, eixos } = relatorio;
  const emRisco = eixos.filter((e) => e.risco !== "baixo");
  return (
    <section id="resumo" className="surface quebra-evitar mt-6 p-6 sm:p-7" aria-labelledby="titulo-resumo">
      <h2 id="titulo-resumo" className="font-display text-xl font-bold text-titulo">
        Resumo executivo
      </h2>
      <div className="mt-4 grid gap-6 md:grid-cols-2">
        <div>
          <p className="flex flex-wrap items-center gap-2" data-nivel-geral={nivel.nivel}>
            <Selo nivel={nivel.nivel} texto={`${nivel.emoji} ${nivel.rotulo}`} />
          </p>
          <p className="mt-2 text-sm leading-relaxed text-tinta">{nivel.descricao}</p>
          <p className="mt-5 text-sm font-semibold text-cinza">Pontuação geral</p>
          <p className="font-display text-4xl font-bold text-titulo" data-percentual>
            {percentual.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
          </p>
          <div className="mt-2">
            <Barra percentual={percentual} nivel={nivel.nivel} />
          </div>
          {eixos.length > 0 && (
            <div className="mt-5 rounded-xl bg-superficie-2 p-4">
              <p className="text-xs font-bold tracking-wider text-cinza uppercase">Considerações</p>
              {emRisco.length === 0 ? (
                <p className="mt-2 text-sm text-tinta">Todos os eixos estão em nível adequado.</p>
              ) : (
                <ul className="mt-2 space-y-1.5 text-sm text-tinta" data-consideracoes>
                  {emRisco.map((e) => (
                    <li key={e.eixo}>
                      <span aria-hidden="true">{e.emoji} </span>
                      <strong className="font-semibold">{e.eixo}</strong> — risco {EM_PALAVRA[e.risco]} ({Math.round(e.percentual)}%)
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        {eixos.length > 0 && (
          <div className="flex flex-col items-center justify-center">
            <Radar eixos={eixos} />
            <p className="mt-2 text-center text-xs text-cinza">
              Alto risco abaixo de 40% · médio de 40% a 70% · baixo a partir de 70%
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * O radar dos seis eixos. É `aria-hidden`: os mesmos números estão escritos ao lado, nas
 * considerações e no mapa de vulnerabilidades, e um leitor de tela os lê lá.
 */
function Radar({ eixos }: { eixos: EixoDoRelatorio[] }) {
  const c = 150;
  const raio = 105;
  const n = eixos.length;
  const ponto = (i: number, p: number) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    const r = (raio * p) / 100;
    return [c + r * Math.cos(a), c + r * Math.sin(a)] as const;
  };
  const poligono = (p: (i: number) => number) =>
    Array.from({ length: n }, (_, i) => ponto(i, p(i)).map((v) => v.toFixed(1)).join(",")).join(" ");

  return (
    <svg viewBox="0 0 300 300" className="w-full max-w-[300px]" aria-hidden="true" data-radar>
      {[25, 50, 75, 100].map((anel) => (
        <polygon key={anel} points={poligono(() => anel)} fill="none" stroke="var(--cf-borda)" strokeWidth="1" />
      ))}
      {eixos.map((_, i) => {
        const [x, y] = ponto(i, 100);
        return <line key={i} x1={c} y1={c} x2={x} y2={y} stroke="var(--cf-borda)" strokeWidth="1" />;
      })}
      <polygon
        points={poligono((i) => eixos[i].percentual)}
        fill="var(--cf-acento)"
        fillOpacity="0.22"
        stroke="var(--cf-acento)"
        strokeWidth="2"
      />
      {eixos.map((e, i) => {
        const [x, y] = ponto(i, 122);
        return (
          <text key={e.eixo} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="20">
            {e.emoji}
          </text>
        );
      })}
    </svg>
  );
}

function Vulnerabilidades({ eixos }: { eixos: EixoDoRelatorio[] }) {
  return (
    <section id="vulnerabilidades" className="mt-8" aria-labelledby="titulo-vulnerabilidades">
      <h2 id="titulo-vulnerabilidades" className="font-display text-xl font-bold text-titulo">
        Mapa de vulnerabilidades da Reforma
      </h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {eixos.map((e) => (
          <div key={e.eixo} className={`surface quebra-evitar border-l-4 p-5 ${TOM[e.risco].caixa}`} data-eixo={e.eixo}>
            <p className="font-semibold text-tinta">
              <span aria-hidden="true">{e.emoji} </span>
              {e.eixo}
            </p>
            <div className="mt-2 flex items-center justify-between gap-3">
              <Selo nivel={e.risco} texto={`Risco ${EM_PALAVRA[e.risco]}`} />
              <span className="text-sm font-semibold text-tinta">{Math.round(e.percentual)}%</span>
            </div>
            <div className="mt-2">
              <Barra percentual={e.percentual} nivel={e.risco} />
            </div>
            {e.titulo && <p className="mt-3 text-sm font-semibold text-tinta">{e.titulo}</p>}
            {(e.implicacoes ?? []).length > 0 && (
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-cinza">
                {(e.implicacoes ?? []).map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function contarPorNivel(respostas: RespostaDoRelatorio[]): Record<Nivel, number> {
  const contagem: Record<Nivel, number> = { alto: 0, medio: 0, baixo: 0 };
  for (const r of respostas) if (r.nivel) contagem[r.nivel] += 1;
  return contagem;
}

function Alertas({ relatorio }: { relatorio: Relatorio }) {
  const contagem = contarPorNivel(relatorio.respostas);
  return (
    <section id="alertas" className="mt-8" aria-labelledby="titulo-alertas">
      <h2 id="titulo-alertas" className="font-display text-xl font-bold text-titulo">
        Alertas de risco
      </h2>
      <p className="mt-1 text-sm text-cinza">
        {relatorio.respostas.length} pontos analisados, um por resposta.
      </p>
      <p className="mt-3 flex flex-wrap gap-2" data-contagem-alertas>
        {(["alto", "medio", "baixo"] as Nivel[])
          .filter((n) => contagem[n] > 0)
          .map((n) => (
            <Selo key={n} nivel={n} texto={`${contagem[n]} de ${EM_PALAVRA[n]} risco`} />
          ))}
      </p>
      <div className="mt-4 space-y-5">
        {relatorio.eixos.map((e) => {
          const doEixo = relatorio.respostas.filter((r) => r.eixo === e.eixo);
          if (doEixo.length === 0) return null;
          return (
            <div key={e.eixo} data-alertas-do-eixo={e.eixo}>
              <h3 className="text-base font-semibold text-tinta">
                <span aria-hidden="true">{e.emoji} </span>
                {e.eixo}
              </h3>
              <ul className="mt-2 space-y-3">
                {doEixo.map((r) => (
                  <li key={r.pergunta} className={`surface quebra-evitar border-l-4 p-4 ${r.nivel ? TOM[r.nivel].caixa : ""}`} data-alerta={r.pergunta}>
                    <div className="flex flex-wrap items-center gap-2">
                      {r.nivel && <Selo nivel={r.nivel} />}
                      <span className="text-xs text-cinza">Pergunta {r.pergunta}</span>
                    </div>
                    <p className="mt-2 text-sm text-tinta">{r.texto}</p>
                    <p className="mt-1 text-sm text-cinza">
                      Sua resposta: <span className="font-semibold text-tinta">{r.resposta}</span>
                    </p>
                    {r.alerta && <p className="mt-2 rounded-lg bg-superficie-2 p-3 text-sm leading-relaxed text-tinta">{r.alerta}</p>}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Plano({ relatorio }: { relatorio: Relatorio }) {
  return (
    <section id="plano" className="mt-8" aria-labelledby="titulo-plano">
      <h2 id="titulo-plano" className="font-display text-xl font-bold text-titulo">
        Plano de ação
      </h2>
      <p className="mt-1 text-sm text-cinza">Ações práticas organizadas em 30, 60 e 90 dias, das de maior risco primeiro.</p>
      <div className="mt-4 space-y-6">
        {FAIXAS.map((f) => {
          const itens = relatorio.respostas
            .filter((r) => r.faixa === f.chave && r.nivel)
            .sort((a, b) => ORDEM[a.nivel as Nivel] - ORDEM[b.nivel as Nivel] || a.pergunta - b.pergunta);
          if (itens.length === 0) return null;
          return (
            <div key={f.chave} data-faixa={f.chave}>
              <h3 className="text-base font-semibold text-tinta">
                {f.titulo} <span className="font-normal text-cinza">— {f.subtitulo}</span>
              </h3>
              <ul className="mt-2 space-y-3">
                {itens.map((r) => (
                  <li key={r.pergunta} className={`surface quebra-evitar border-l-4 p-4 ${TOM[r.nivel as Nivel].caixa}`} data-acao-da-pergunta={r.pergunta}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Selo nivel={r.nivel as Nivel} />
                      <span className="text-xs text-cinza">{r.eixo}</span>
                    </div>
                    <p className="mt-2 text-sm font-semibold text-tinta">{r.texto}</p>
                    {r.alerta && (
                      <p className="mt-1 text-sm text-cinza">
                        <span className="font-semibold text-tinta">Contexto: </span>
                        {r.alerta}
                      </p>
                    )}
                    {(r.acoes ?? []).length > 0 && (
                      <>
                        <p className="mt-3 text-xs font-bold tracking-wider text-cinza uppercase">O que fazer</p>
                        <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-tinta">
                          {(r.acoes ?? []).map((a) => (
                            <li key={a}>{a}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Indicadores() {
  return (
    <section id="indicadores" className="surface quebra-evitar mt-8 p-6" aria-labelledby="titulo-indicadores">
      <h2 id="titulo-indicadores" className="font-display text-xl font-bold text-titulo">
        Indicadores de sucesso
      </h2>
      <p className="mt-4 text-sm font-semibold text-tinta">Metas essenciais</p>
      <ul className="mt-2 grid gap-2 sm:grid-cols-2">
        {METAS.map((m) => (
          <li key={m} className="flex items-center justify-between rounded-lg bg-superficie-2 px-3 py-2.5 text-sm text-tinta">
            {m}
            <span className="font-semibold">100%</span>
          </li>
        ))}
      </ul>
      <p className="mt-5 text-sm font-semibold text-tinta">Resultados esperados</p>
      <ul className="mt-2 space-y-2">
        {RESULTADOS.map((r) => (
          <li key={r} className="rounded-lg bg-superficie-2 px-3 py-2.5 text-sm text-tinta">
            {r}
          </li>
        ))}
      </ul>
    </section>
  );
}
